import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Clock } from "lucide-react";
import {
  useCheckInSend,
  useCheckInSelector,
} from "../context";
import { checkInSelectors } from "../selectors";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { useAudio } from "../../../../../lib/use-audio";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { Tile } from "../../../components/Tile";
import { KioskStep } from "../../../../../components/kiosk-step";
import {
  KioskLoadingPanel,
  KioskStatePanel,
} from "../../../../../components/kiosk-state-panel";

interface Appointment {
  scheduleId: string;
  time: string;
  type: string;
}

/**
 * Appointment selection step when a patient has multiple appointments today.
 * The time is the headline of each choice, since it is what tells two
 * appointments of the same type apart (KC-26).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <AppointmentSelectionStep />
 * ```
 */
export function AppointmentSelectionStep() {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const patientId = useCheckInSelector(checkInSelectors.patientId);
  const appointments = useCheckInSelector(checkInSelectors.appointments) ?? [];
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  // Play audio on mount
  useAudio("checkIn-appointmentSelection");

  const handleSelect = async (appointment: Appointment) => {
    if (!patientId) return;

    setIsLoading(true);
    setError(undefined);
    try {
      // Perform check-in for selected appointment
      await docCirrusRpcClient.frontdesk.checkIn({
        patientId,
        scheduleId: appointment.scheduleId,
      });
      send({
        type: "APPOINTMENT_SELECTED",
        scheduleId: appointment.scheduleId,
      });
    } catch (err) {
      frontdeskDiagnosticLogger.error(
        "Check-in for selected appointment failed",
        {
          error: err,
          hasPatientId: Boolean(patientId),
          hasScheduleId: Boolean(appointment.scheduleId),
        },
      );
      setError(t("self-service.check-in.appointment-selection.error-v2"));
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <KioskStep
        width="narrow"
        align="center"
        title={t("self-service.check-in.appointment-selection.title")}
      >
        <KioskLoadingPanel title={t("self-service.check-in.checking-in.loading")} />
      </KioskStep>
    );
  }

  return (
    <KioskStep
      width="default"
      title={t("self-service.check-in.appointment-selection.title")}
      lead={t("self-service.check-in.appointment-selection.subtitle")}
    >
      {error ? (
        <KioskStatePanel
          tone="error"
          title={error}
          testId="frontdesk-appointment-selection-error"
        />
      ) : null}
      <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
        {appointments.map((appointment) => (
          <Tile
            key={appointment.scheduleId}
            tone="blue-subtle"
            className="min-h-40"
            data-testid={`appointment-${appointment.scheduleId}`}
            name={t("self-service.check-in.appointment-selection.time", {
              time: appointment.time,
            })}
            desc={appointment.type}
            icon={<Clock />}
            onClick={() => void handleSelect(appointment)}
          />
        ))}
      </div>
    </KioskStep>
  );
}
